import React, { useEffect, useState } from "react";
import "./EmailUnsubscribes.css";
import api from "../../services/api";
import { toast } from "react-toastify";
import { formatAdminDateTime } from "../../utils/datetime";

const EmailUnsubscribes = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ total: 0, page: 1, limit: 20, totalPages: 1 });
  const [search, setSearch] = useState("");
  const [searchDebounce, setSearchDebounce] = useState("");

  // Debounce search
  useEffect(() => {
    const t = setTimeout(() => setSearchDebounce(search), 400);
    return () => clearTimeout(t);
  }, [search]);

  const fetchData = async (page = 1) => {
    setLoading(true);
    try {
      const res = await api.get("/email/unsubscribe/list", {
        params: { page, limit: 20, search: searchDebounce },
      });
      setData(res.data || []);
      setPagination(res.pagination || { total: 0, page: 1, limit: 20, totalPages: 1 });
    } catch (err) {
      console.error("Fetch error:", err);
      toast.error("Failed to load unsubscribes");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData(1);
  }, [searchDebounce]);

  const handleResubscribe = async (id, email) => {
    if (!window.confirm(`Re-subscribe ${email}? They will start receiving emails again.`)) return;

    try {
      await api.delete(`/email/unsubscribe/${id}`);
      toast.success(`${email} re-subscribed successfully`);
      fetchData(pagination.page);
    } catch (err) {
      toast.error("Failed to re-subscribe");
    }
  };

  const handlePageChange = (newPage) => {
    if (newPage < 1 || newPage > pagination.totalPages) return;
    fetchData(newPage);
  };

  return (
    <div className="unsub-admin-page">
      <div className="unsub-admin-header">
        <div className="unsub-admin-title-row">
          <div>
            <h1 className="unsub-admin-title">Email Unsubscribes</h1>
            <p className="unsub-admin-subtitle">
              Users who opted out of broadcast emails. Transactional emails are unaffected.
            </p>
          </div>
          <div className="unsub-admin-count-badge">
            {pagination.total} total
          </div>
        </div>

        <div className="unsub-admin-toolbar">
          <div className="unsub-admin-search">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#999" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="11" cy="11" r="8" />
              <line x1="21" y1="21" x2="16.65" y2="16.65" />
            </svg>
            <input
              type="text"
              placeholder="Search by email..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
        </div>
      </div>

      <div className="unsub-admin-table-wrapper">
        <table className="unsub-admin-table">
          <thead>
            <tr>
              <th>#</th>
              <th>Email</th>
              <th>Reason</th>
              <th>Unsubscribed At</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan="5" className="unsub-admin-empty">
                  <div className="unsub-admin-spinner" />
                  Loading...
                </td>
              </tr>
            ) : data.length === 0 ? (
              <tr>
                <td colSpan="5" className="unsub-admin-empty">
                  <div className="unsub-admin-empty-icon">📭</div>
                  {search ? "No results found" : "No unsubscribed emails yet"}
                </td>
              </tr>
            ) : (
              data.map((item, idx) => (
                <tr key={item.id}>
                  <td className="unsub-admin-row-num">
                    {(pagination.page - 1) * pagination.limit + idx + 1}
                  </td>
                  <td className="unsub-admin-email">{item.email}</td>
                  <td className="unsub-admin-reason">{item.reason || "—"}</td>
                  <td className="unsub-admin-date">
                    {item.unsubscribed_at ? formatAdminDateTime(item.unsubscribed_at) : "—"}
                  </td>
                  <td>
                    <button
                      className="unsub-admin-resub-btn"
                      onClick={() => handleResubscribe(item.id, item.email)}
                      title="Re-subscribe this email"
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="1 4 1 10 7 10" />
                        <path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10" />
                      </svg>
                      Re-subscribe
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {pagination.totalPages > 1 && (
        <div className="unsub-admin-pagination">
          <button
            disabled={pagination.page <= 1}
            onClick={() => handlePageChange(pagination.page - 1)}
          >
            ← Prev
          </button>
          <span className="unsub-admin-page-info">
            Page {pagination.page} of {pagination.totalPages}
          </span>
          <button
            disabled={pagination.page >= pagination.totalPages}
            onClick={() => handlePageChange(pagination.page + 1)}
          >
            Next →
          </button>
        </div>
      )}
    </div>
  );
};

export default EmailUnsubscribes;
